import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { getRevstackAiInsights, sendRequest } from "@ts/retrieve-data";
import { stopStream } from "@workers/request.worker";
import { showToast } from "@ts/modals";
import { CA_DD_MODULES } from "@ts/common-types";
import {
    updateAiInsightsData,
    updateIsAiInsightsLoading,
} from "@features/revstackWidgetsSlice";
import store from "@/store";

interface StreamConfig {
    resetWidgetDataAction: () => any;
    updateWidgetDataAction: (data: any) => any;
    updateAiInsightsAction?: (payload: any) => any;
    aiInsightsKey?: string;
}

interface UseRevstackStreamDataParams {
    // Option 1: Pass config array (for components with tabKey-based separation)
    config?: any[];

    // Option 2: Pass pre-separated queries (for layout components)
    overviewQueries?: any[];
    otherTabsQueries?: any[];

    streamConfig: StreamConfig;
    overviewTabKey?: string;
}

interface UseRevstackStreamDataReturn {
    getRevstackWidgetStreamData: () => Promise<void>;
    controllerRef: React.RefObject<AbortController | null>;
}

export const useRevstackStreamData = ({
    config,
    overviewQueries,
    otherTabsQueries,
    streamConfig,
    overviewTabKey = CA_DD_MODULES.OVERVIEW,
}: UseRevstackStreamDataParams): UseRevstackStreamDataReturn => {
    const dispatch = useDispatch();
    const controllerRef = useRef<AbortController | null>(null);
    const overviewControllerRef = useRef<AbortController | null>(null);
    const otherControllerRef = useRef<AbortController | null>(null);

    const fetchAiInsights = async () => {
        try {
            //!Note[DND]: This is to get the latest state of the ai insights payload without adding dependency
            const aiInsightsPayload = store.getState().aiInsightsPayload.value;
            const payload = {
                filters: aiInsightsPayload?.filters,
                data: aiInsightsPayload?.data?.[streamConfig?.aiInsightsKey],
            };
            const data = await getRevstackAiInsights(payload);
            dispatch(
                updateAiInsightsData({
                    [streamConfig?.aiInsightsKey]: data,
                })
            );
        } catch (error: any) {
            showToast("danger", error?.message);
            console.error(error);
        } finally {
            dispatch(updateIsAiInsightsLoading(false));
        }
    };

    const handleStreamUpdation = useCallback(
        (
            type: string,
            parsedStreamData: any,
            requestController?: AbortController
        ) => {
            if (
                parsedStreamData.message === "done" ||
                parsedStreamData?.event === "end"
            ) {
                // Only stop the specific controller for this request
                if (requestController) {
                    stopStream(requestController);
                }

                if (
                    (type === overviewTabKey ||
                        type === CA_DD_MODULES.OVERVIEW) &&
                    parsedStreamData?.event === "end"
                ) {
                    fetchAiInsights();
                }

                return;
            }

            dispatch(streamConfig.updateWidgetDataAction(parsedStreamData));

            if (
                (type === overviewTabKey || type === CA_DD_MODULES.OVERVIEW) &&
                streamConfig.updateAiInsightsAction &&
                streamConfig.aiInsightsKey
            ) {
                dispatch(
                    streamConfig.updateAiInsightsAction({
                        [streamConfig.aiInsightsKey]: parsedStreamData,
                    })
                );
            }
        },
        [dispatch, streamConfig, overviewTabKey]
    );

    const getRevstackWidgetStreamData = useCallback(async () => {
        dispatch(updateIsAiInsightsLoading(true));

        let finalOverviewQueries: any[] = [];
        let finalOtherQueries: any[] = [];

        // Determine query separation method
        if (config) {
            // Option 1: Separate queries based on tabKey from config
            const {
                overviewQueries: configOverviewQueries,
                otherQueries: configOtherQueries,
            } = config.reduce(
                (acc: any, configItem: any) => {
                    if (configItem.tabKey === overviewTabKey) {
                        acc.overviewQueries.push(...configItem.cubeQueries);
                    } else {
                        acc.otherQueries.push(...configItem.cubeQueries);
                    }
                    return acc;
                },
                { overviewQueries: [], otherQueries: [] }
            );

            finalOverviewQueries = configOverviewQueries;
            finalOtherQueries = configOtherQueries;
        } else if (overviewQueries && otherTabsQueries) {
            // Option 2: Use pre-separated queries
            finalOverviewQueries = overviewQueries;
            finalOtherQueries = otherTabsQueries;
        } else {
            console.warn(
                "useRevstackStreamData: Either config or separated queries must be provided"
            );
            return;
        }

        // Reset state and cleanup previous controllers
        dispatch(streamConfig.resetWidgetDataAction());

        // Stop any existing streams
        stopStream(controllerRef.current);
        stopStream(overviewControllerRef.current);
        stopStream(otherControllerRef.current);

        // Create separate controllers for each request
        const overviewController = new AbortController();
        const otherController = new AbortController();
        overviewControllerRef.current = overviewController;
        otherControllerRef.current = otherController;
        controllerRef.current = overviewController;

        try {
            const requests = [];
            const cubeRole = sessionStorage.getItem("cube_role");

            // Overview queries request
            if (finalOverviewQueries.length > 0) {
                requests.push(
                    sendRequest(
                        {
                            type: "category-analysis",
                            method: "POST",
                            data: finalOverviewQueries,
                        },
                        true,
                        false,
                        true,
                        "application/json",
                        {
                            isStreaming: true,
                            onMessage: (parsedStreamData: any) =>
                                handleStreamUpdation(
                                    overviewTabKey === CA_DD_MODULES.OVERVIEW
                                        ? overviewTabKey
                                        : CA_DD_MODULES.OVERVIEW,
                                    parsedStreamData,
                                    overviewController
                                ),
                            ...(otherTabsQueries && { onClose: () => {} }), // Add onClose for layout pattern
                            signal: overviewController.signal,
                        },
                        {
                            cube_role: cubeRole,
                        }
                    )
                );
            }

            // Other tabs queries request
            if (finalOtherQueries.length > 0) {
                requests.push(
                    sendRequest(
                        {
                            type: "category-analysis",
                            method: "POST",
                            data: finalOtherQueries,
                        },
                        true,
                        false,
                        true,
                        "application/json",
                        {
                            isStreaming: true,
                            onMessage: (parsedStreamData: any) =>
                                handleStreamUpdation(
                                    otherTabsQueries ? "otherTabs" : "",
                                    parsedStreamData,
                                    otherController
                                ),
                            ...(otherTabsQueries && { onClose: () => {} }), // Add onClose for layout pattern
                            signal: otherController.signal,
                        },
                        {
                            cube_role: cubeRole,
                        }
                    )
                );
            }

            if (requests.length > 0) {
                await Promise.all(requests);
            }
        } catch (error: any) {
            const errorMessage = error?.message || "Something went wrong";
            console.error(
                "Error in getRevstackWidgetStreamData:",
                errorMessage
            );
            showToast("error", errorMessage);
        }
    }, [
        config,
        overviewQueries,
        otherTabsQueries,
        streamConfig,
        overviewTabKey,
        dispatch,
        handleStreamUpdation,
    ]);

    return {
        getRevstackWidgetStreamData,
        controllerRef,
    };
};

export default useRevstackStreamData;
